import { useState } from 'react';

import { IBookingAddOnInstance } from '@/booking-add-ons/models';
import {
  selectHotelConfigurationByReferenceIdOrFail,
  useActiveBrandConfig,
} from '@/brand';

import { AddOnError } from '../errors';
import { useUpdateBookingAddOnsMutation } from './use-update-booking-add-ons-mutation.hook';

export const useUpdateBookingAddOn = () => {
  const { mutateAsync, isLoading, isSuccess } =
    useUpdateBookingAddOnsMutation();
  const [error, setError] = useState<AddOnError>();
  const [addOnBeingUpdated, setAddOnBeingUpdated] = useState<
    string | undefined
  >();
  const brandConfig = useActiveBrandConfig();

  return {
    async updateAddOn(
      {
        id,
        quantity,
        bookingId,
        hotelReferenceId,
        previousAddOns,
      }: {
        id: string;
        quantity: number;
        bookingId: string;
        hotelReferenceId: string;
        previousAddOns: Pick<
          IBookingAddOnInstance,
          'id' | 'requestedQuantity'
        >[];
      },
      options?: Parameters<typeof mutateAsync>[1]
    ) {
      setAddOnBeingUpdated(id);
      const { dialect } = selectHotelConfigurationByReferenceIdOrFail(
        brandConfig,
        hotelReferenceId
      );

      const newAddOns = [
        ...previousAddOns
          .map((addOn) => ({
            id: addOn.id,
            quantity: addOn.requestedQuantity,
          }))
          // Remove targeted add-on first, then re-add with updated quantity.
          .filter((addOn) => addOn.id !== id),
        ...(quantity > 0 ? [{ id, quantity }] : []),
      ];

      try {
        const result = await mutateAsync(
          {
            body: {
              hotelReferenceId,
              bookingId,
              addOns: newAddOns,
            },
            dialect,
          },
          {
            ...options,
            onSettled: async (...args) => {
              options?.onSettled?.(...args);
              setAddOnBeingUpdated(undefined);
            },
          }
        );
        return result;
      } catch (error) {
        setError(error);
        console.error(error);
        return;
      }
    },
    isLoading,
    addOnBeingUpdated,
    isSuccess,
    error,
    clearError() {
      setError(undefined);
    },
  };
};
